import { ApiError, ApiException } from '@/utils/errorHandler';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://www.getpoints.com.br';

async function apiFetch<T>(method: string, endpoint: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const json = await res.json().catch(() => null);

    if (!res.ok) {
      const errorPayload: ApiError = {
        timestamp: json?.timestamp || new Date().toISOString(),
        status: json?.status || res.status,
        error: json?.error || 'Unexpected Error',
        message: json?.message || 'Erro inesperado',
        path: json?.path || endpoint,
        traceId: json?.traceId || 'unknown',
        details: json?.details || [],
      };
      throw new ApiException(errorPayload);
    }

    return json as T;
  } catch (err) {
    // Re-throw ApiException as-is
    if (err instanceof ApiException) {
      throw err;
    }

    // Convert network errors to ApiException
    if (err instanceof Error) {
      const errorPayload: ApiError = {
        timestamp: new Date().toISOString(),
        status: 0,
        error: 'Network Error',
        message: err.message === 'Failed to fetch' || err.message === 'NetworkError when attempting to fetch resource.'
          ? 'Servidor indisponível no momento. Verifique sua conexão e tente novamente.'
          : err.message,
        path: endpoint,
        traceId: 'unknown',
        details: ['Erro de conexão. Verifique se o servidor está online ou se há problemas de rede.'],
      };
      throw new ApiException(errorPayload);
    }

    throw err;
  }
}

// ─── SERPRO CPF ───
export interface SerproCpfResponse {
  responseJson: string | unknown;
}

export async function consultarCPF(cpf: string): Promise<SerproCpfResponse> {
  return apiFetch<SerproCpfResponse>('GET', `/api/v1/integrations/serpro/cpf/${cpf}`);
}

// ─── SERPRO CNPJ ───
export interface SerproCnpjResponse {
  responseJson: string | unknown;
}

export async function consultarCNPJ(cnpj: string): Promise<SerproCnpjResponse> {
  return apiFetch<SerproCnpjResponse>('GET', `/api/v1/integrations/serpro/cnpj/${cnpj}`);
}

// ─── Start Registration ───
export interface StartRegistrationPayload {
  tipoDocumento: 'cpf' | 'cnpj';
  documento: string;
  dataNascimento: string;
  deviceFingerprint: string;
}

export interface StartRegistrationResponse {
  registrationId: string;
  nome?: string;
  email?: string;
  celular?: string;
}

export async function startRegistration(payload: StartRegistrationPayload): Promise<StartRegistrationResponse> {
  return apiFetch<StartRegistrationResponse>('POST', '/api/v1/account-opening/start', payload);
}

// ─── OTP Send ───
export interface SendOtpPayload {
  canal: 'EMAIL' | 'SMS';
  destino: string;
  name: string;
  deviceFingerprint: string;
}

export interface SendOtpResponse {
  otpToken: string;
}

export async function sendOtp(payload: SendOtpPayload): Promise<SendOtpResponse> {
  return apiFetch<SendOtpResponse>('POST', '/api/v1/account-opening/otp/send', payload);
}

// ─── OTP Validate ───
export interface ValidateOtpPayload {
  canal: 'EMAIL' | 'SMS';
  destino: string;
  codigo: string;
  otpToken: string;
  deviceFingerprint: string;
}

export interface ValidateOtpResponse {
  validated: boolean;
}

export async function validateOtp(payload: ValidateOtpPayload): Promise<ValidateOtpResponse> {
  return apiFetch<ValidateOtpResponse>('POST', '/api/v1/account-opening/otp/validate', payload);
}

// ─── Finalize Registration ───
export interface FinalizeRegistrationPayload {
  tipoDocumento: 'cpf' | 'cnpj';
  documento: string;
  dataNascimento?: string;
  nome: string;
  email: string;
  celular: string;
  senha: string;
  confirmarSenha: string;
  deviceFingerprint: string;
  emailOtpToken: string;
  celularOtpToken: string;
}

export async function finalizeRegistration(payload: FinalizeRegistrationPayload): Promise<void> {
  return apiFetch<void>('POST', '/api/v1/account-opening', payload);
}